import { useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { MockBanner } from "@/components/layout/MockBanner";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useNotificationsConnection } from "@/features/notifications/hooks/useNotificationsConnection";
import { cn } from "@/lib/utils/cn";

export function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const location = useLocation();

  // Liga o stream SSE de notificações ao serviço central (uma única vez no layout
  // autenticado). O hook gateia internamente por isAuthenticated + presença de token,
  // então é seguro chamá-lo antes dos early returns (regras de hooks do React).
  useNotificationsConnection();

  // Em refresh/deep-link o token só é reidratado após o refresh silencioso
  // (`initAuth`). Aguardamos o bootstrap antes de decidir — sem isso o layout
  // mandava para /login e perdia a rota pedida (bug #19).
  if (!bootstrapped) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand-500"
          role="status"
          aria-label="Carregando"
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    // zoom 0.85 reduz a densidade ~15%; minHeight compensa o zoom pra o layout
    // (e a sidebar dark) preencher a viewport inteira, sem faixa clara no rodapé.
    <div className="flex bg-bg" style={{ zoom: 0.85, minHeight: "calc(100vh / 0.85)" }}>
      {/* Desktop sidebar */}
      <div
        className="sticky top-0 hidden shrink-0 lg:block"
        style={{ height: "calc(100vh / 0.85)" }}
      >
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-40 lg:hidden",
          drawerOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <div
          className={cn(
            "absolute inset-0 bg-black/50 transition-opacity",
            drawerOpen ? "opacity-100" : "opacity-0",
          )}
          onClick={() => setDrawerOpen(false)}
        />
        <div
          className={cn(
            "absolute inset-y-0 left-0 h-full transition-transform",
            drawerOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <Sidebar onItemClick={() => setDrawerOpen(false)} />
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <MockBanner />
        <Topbar onMenuClick={() => setDrawerOpen(true)} />
        <main className="flex-1 overflow-y-auto p-2.5 md:p-4 lg:p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
